import { IonInput, IonItem, IonRadio, IonRadioGroup } from "@ionic/react";
import { DateTime } from "luxon";
import { Asterisk, Colors, fontMedium } from "swing-components";

import {
  CancelReason,
  CancelReasonType,
  ModalCancelCommitmentsBody,
  ModalCancelCommitmentsFooter,
  ModalCancelCommitmentsHeader,
  type CancelFormState,
  type PenaltyAlert,
} from "~components";
import { calcRemainingDays, msg, type PartialCancelRequestPayload, type RequestData } from "~utils";

type ModalCancelCommitmentsCancellationReasonProps = {
  cancelFormData: CancelFormState;
  penaltyType: PenaltyAlert;
  onDismiss: () => void;
  onConfirm: (cancelPayload: CancelFormState | PartialCancelRequestPayload) => void;
  setCancelFormData: (cancelFormData: CancelFormState) => void;
  hasApiError?: boolean;
  intervals: RequestData["intervals"];
  // Optional DateTime to use for testing purposes when calculating remaining days
  // TODO: Remove this once we have a proper way to mock the time in storybook
  overrideCurrentTime?: DateTime;
  isOnConfirmPending?: boolean;
};

export function ModalCancelCommitmentsCancellationReason(
  props: ModalCancelCommitmentsCancellationReasonProps,
) {
  const {
    cancelFormData,
    penaltyType,
    hasApiError,
    onDismiss,
    onConfirm,
    setCancelFormData,
    intervals,
    overrideCurrentTime,
    isOnConfirmPending,
  } = props;

  /********* Render ********/
  return (
    <div className="modal-wrapper">
      <ModalCancelCommitmentsHeader onDismiss={onDismiss} />
      <ModalCancelCommitmentsBody hasApiError={hasApiError} penaltyType={penaltyType}>
        <p css={fontMedium({ fontWeight: 600 })}>
          {msg("CANCEL_COMMITMENT_MODAL_MESSAGE")}
          <Asterisk />
        </p>

        {/* Cancellation reason radio group */}
        {/* TODO: Replace with RadioInput component */}
        <IonRadioGroup
          value={cancelFormData.reason}
          onIonChange={(e) => setCancelFormData({ ...cancelFormData, reason: e.target.value })}
        >
          {Object.keys(CancelReason).map((reason) => {
            return (
              <div key={reason}>
                <IonRadio
                  className="cancel-radio-answer"
                  style={{ "--color": Colors.blue500 }}
                  value={reason}
                  labelPlacement="end"
                >
                  {CancelReason[reason as CancelReasonType]}
                </IonRadio>
              </div>
            );
          })}
        </IonRadioGroup>

        {/* Other reason input */}
        {isOther(cancelFormData.reason) && (
          <IonItem lines="inset" className="ion-no-padding modal-cancel-other-input">
            <IonInput
              placeholder={msg("CANCEL_COMMITMENT_MODAL_OTHER_PLACEHOLDER")}
              onIonInput={(e) =>
                setCancelFormData({ ...cancelFormData, explanation: e.detail.value })
              }
              value={cancelFormData.explanation}
            />
          </IonItem>
        )}
      </ModalCancelCommitmentsBody>
      <ModalCancelCommitmentsFooter
        buttonPrimary={{
          isActionPending: isOnConfirmPending,
          isDisabled: isOnConfirmPending === true || isDisabled(cancelFormData),
          // TODO: Remove FF check once the feature is stable
          text: msg(
            "CANCEL_COMMITMENT_MODAL_BUTTON_SUBMIT_LABEL",
            cancelFormData.partialCancelText
              ? { isPartialCancel: true }
              : {
                  isPartialCancel: false,
                  remainingDays: calcRemainingDays(intervals, overrideCurrentTime),
                },
          ),
          onClick: () => {
            // clear the explanation field if option isn't "Other"
            // Note: clear / remove explanation field here so user can easily jump between
            // options and keep any provided explanation intact prior to submitting
            if (!isOther(cancelFormData.reason)) {
              // do not include in payload
              delete cancelFormData.explanation;
            }
            onConfirm(cancelFormData);
          },
        }}
        buttonSecondary={{
          isDisabled: isOnConfirmPending === true,
          text: msg("CANCEL_COMMITMENT_MODAL_BUTTON_CANCEL"),
          onClick: onDismiss,
        }}
      />
    </div>
  );
}

/********* Helper functions ********/
export function isDisabled(cancelFormData: CancelFormState) {
  return (
    !cancelFormData.reason ||
    (isOther(cancelFormData.reason) && !cancelFormData.explanation?.trim())
  );
}

function isOther(cancelReason: CancelReasonType | undefined): boolean {
  return !!cancelReason && CancelReason[cancelReason] === CancelReason.CANCEL_REASON_OTHER;
}
